// InventoryPanel.tsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    TextField,
    LinearProgress,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ViewListIcon from '@mui/icons-material/ViewList';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import LabelIcon from '@mui/icons-material/Label';
import HistoryIcon from '@mui/icons-material/History';
import { publicInstance, logdbInstance } from '../axiosInstance';
import { format, subDays, addMinutes } from 'date-fns';
import { DeviceDetails, DeviceInfo, OperatorCounts, CalibrationType } from './types';
import { determineOperator, metricOptions, uptimeToMinutes } from './utils';
import TestDevicesList from './TestDevicesList';
import DeviceCharts from './DeviceCharts';
import AddDeviceForm from './AddDeviceForm';
import ScriptsPanel from './ScriptsPanel';
import LabelCharts from './LabelChartsComponent';

const InventoryPanel = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [processedDevices, setProcessedDevices] = useState<DeviceInfo[]>([]);
    const [serviceDevices, setServiceDevices] = useState<DeviceInfo[]>([]);
    const [operatorCounts, setOperatorCounts] = useState<OperatorCounts>({
        Orange: 0,
        Play: 0,
        Plus: 0,
        Unknown: 0
    });
    const [activeView, setActiveView] = useState<'list' | 'charts' | 'labelCharts'>('list');
    const [selectedMetric, setSelectedMetric] = useState<string>('loadUptime');
    const [deviceChartData, setDeviceChartData] = useState<{ [key: string]: any[] }>({});
    const [deviceLoadings, setDeviceLoadings] = useState<{ [key: string]: boolean }>({});
    const [devicesForCharts, setDevicesForCharts] = useState<DeviceInfo[]>([]);
    const [chartsPerRow, setChartsPerRow] = useState<number>(3);
    const [timeRange, setTimeRange] = useState<number>(1);
    const isDarkMode = theme.name === 'dark';
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
    const [addDeviceDialogOpen, setAddDeviceDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [scriptsPanelOpen, setScriptsPanelOpen] = useState(false);
    const [filterListBySelection, setFilterListBySelection] = useState(false);

    // Refs do kontroli stanu komponentu
    const isMounted = useRef(true);
    const initialFetchDone = useRef(false);
    const abortController = useRef<AbortController | null>(null);
    const previousDevices = useRef<string[]>([]);

    useEffect(() => {
        // Check if user has admin role
        const userRole = localStorage.getItem('role');
        setIsAdmin(userRole === 'admin');
    }, []);

    // Fetch device data function
    const fetchDeviceData = useCallback(async (boxNumber: string, days: number = timeRange) => {
        if (!isMounted.current || !abortController.current) return;

        try {
            setDeviceLoadings(prev => ({ ...prev, [boxNumber]: true }));

            const endDate = new Date();
            const startDate = subDays(endDate, days);
            const adjustedStartDate = new Date(startDate);
            adjustedStartDate.setHours(adjustedStartDate.getHours() - 1);

            const response = await logdbInstance.get(`/log`, {
                params: {
                    box: `eq.box${boxNumber}`,
                    time: [
                        `gte.${format(adjustedStartDate, "yyyy-MM-dd'T'HH:mm:ss")}`,
                        `lt.${format(endDate, "yyyy-MM-dd'T'HH:mm:ss")}`
                    ]
                },
                signal: abortController.current.signal
            });

            if (!isMounted.current) return;

            if (Array.isArray(response.data)) {
                const formattedData = response.data.map((item: any) => {
                    const adjustedTime = new Date(item.time);
                    adjustedTime.setHours(adjustedTime.getHours() + 1);
                    
                    return {
                        time: adjustedTime,
                        avg5mload: parseFloat(item.obj.avg5mload || '0'),
                        uptime: uptimeToMinutes(item.obj.uptime),
                        rawUptime: item.obj.uptime,
                        PM10: parseFloat(item.obj.PM10 || '0'),
                        PM25: parseFloat(item.obj.PM25 || '0'),
                        AMBIENT_TEMP: parseFloat(item.obj.AMBIENT_TEMP || '0'),
                        AMBIENT_HUMID: parseFloat(item.obj.AMBIENT_HUMID || '0'),
                        AMBIENT_PRESS: parseFloat(item.obj.AMBIENT_PRESS || '0'),
                        JAS_ABS: parseFloat(item.obj.JAS_ABS || '0'),
                        JAS_VALUE: parseFloat(item.obj.JAS_VALUE || '0'),
                        DUST_HEATER_NTC: parseFloat(item.obj.DUST_HEATER_NTC || '0'),
                        NH3_raw: parseFloat(item.obj.NH3_raw || '0'),
                        H2S_raw: parseFloat(item.obj.H2S_raw || '0'),
                        ODO: parseFloat(item.obj.ODO || '0'),
                        rssi: parseFloat(item.obj.rssi || '0'),
                    };
                });

                const fullRange = [];
                let currentDate = new Date(adjustedStartDate);
                
                while (currentDate <= endDate) {
                    fullRange.push({
                        time: new Date(currentDate),
                        avg5mload: null,
                        uptime: null,
                        rawUptime: null,
                        PM10: null,
                        PM25: null,
                        AMBIENT_TEMP: null,
                        AMBIENT_HUMID: null,
                        AMBIENT_PRESS: null,
                        JAS_ABS: null,
                        JAS_VALUE: null,
                        DUST_HEATER_NTC: null,
                        NH3_raw: null,
                        H2S_raw: null,
                        ODO: null,
                        rssi: null,
                    });
                    currentDate = addMinutes(currentDate, 5);
                }

                const mergedData = fullRange.map(rangeItem => {
                    const matchingItem = formattedData.find(dataItem =>
                        Math.abs(dataItem.time.getTime() - rangeItem.time.getTime()) < 150000
                    );
                    return matchingItem || rangeItem;
                });

                if (isMounted.current) {
                    setDeviceChartData(prev => ({
                        ...prev,
                        [boxNumber]: mergedData
                    }));
                    setDeviceLoadings(prev => ({ ...prev, [boxNumber]: false }));
                }
            }
        } catch (error: any) {
            if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
                console.log('Request was cancelled for box:', boxNumber);
            } else if (isMounted.current) {
                console.error(`Error fetching data for box ${boxNumber}:`, error);
                setDeviceLoadings(prev => ({ ...prev, [boxNumber]: false }));
            }
        }
    }, [timeRange]);

    const handleLoadCharts = useCallback(() => {
        if (selectedDevices.length > 0) {
            const devicesToLoad = processedDevices.filter(device => selectedDevices.includes(device.box));
            setDevicesForCharts(devicesToLoad);
            setActiveView('charts');
            
            // Wyczyść poprzednie dane wykresów i załaduj nowe
            setDeviceChartData({});
            devicesToLoad.forEach(device => {
                fetchDeviceData(device.box, timeRange);
            });
        }
    }, [selectedDevices, processedDevices, fetchDeviceData, timeRange]);

    // Funkcja do przeładowania wykresów z nowym time range
    const handleReloadChartsWithTimeRange = useCallback(() => {
        if (devicesForCharts.length > 0) {
            // Wyczyść poprzednie dane wykresów i załaduj nowe z aktualnym timeRange
            setDeviceChartData({});
            devicesForCharts.forEach(device => {
                fetchDeviceData(device.box, timeRange);
            });
        }
    }, [devicesForCharts, fetchDeviceData, timeRange]);

    const fetchCalibrationData = async (devices: DeviceInfo[]): Promise<DeviceInfo[]> => {
        try {
            // Zbierz numery box ze wszystkich urządzeń
            const boxNumbers = devices.map(device => device.box);
            
            if (boxNumbers.length === 0) {
                return devices;
            }
            
            // Pobierz dane kalibracyjne dla wszystkich urządzeń
            const response = await publicInstance.get(`/view_dacapi_calib_info_merged?box=in.(${boxNumbers.join(',')})`);
            
            // Mapuj dane kalibracyjne na urządzenia
            const devicesWithCalibrations = devices.map(device => {
                const calibrationData = response.data.find((item: any) => item.box.toString() === device.box);
                return {
                    ...device,
                    calibrations: calibrationData ? calibrationData.calibrations : []
                };
            });
            
            return devicesWithCalibrations;
        } catch (error) {
            console.error('Error fetching calibration data:', error);
            return devices;
        }
    };

    // Helper function to normalize operator names
    const normalizeOperator = (operator: string | null | undefined): string => {
        if (!operator) return 'Unknown';
        
        const normalized = operator.trim().toLowerCase();
        
        if (normalized === 'orange' || normalized === 'oragne') return 'Orange';
        if (normalized === 'play') return 'Play';
        if (normalized === 'plus') return 'Plus';
        
        // If it's not one of the known operators, return 'Unknown'
        return 'Unknown';
    };

    const fetchTestDevices = async () => {
        setIsLoading(true);
        try {
            const response = await publicInstance.get('/view_dacapi_inventory_all');
            console.log('Raw inventory data sample:', response.data.slice(0, 3).map(d => ({ box: d.box, mobile_operator: d.mobile_operator })));
            
            // Process all devices from the inventory endpoint with complete field mapping
            const processedData = response.data.map((device: any) => ({
                box: device.box.toString(),
                iccid: device.iccid,
                time_stamp: device.time_stamp,
                label: Array.isArray(device.label) ? device.label : [],
                comments: device.comments,
                time_stamps: device.time_stamps,
                status: 'BIURO-TEST',
                operator: normalizeOperator(device.mobile_operator),
                modem: device.modem
            }));

            // Calculate operator counts
            const counts = processedData.reduce((acc: OperatorCounts, device) => {
                const operatorKey = device.operator as keyof OperatorCounts;
                if (acc[operatorKey] !== undefined) {
                    acc[operatorKey]++;
                } else {
                    acc.Unknown++;
                }
                return acc;
            }, { Orange: 0, Play: 0, Plus: 0, Unknown: 0 });

            console.log('Inventory operator counts:', counts);
            setOperatorCounts(counts);
            
            // Pobierz dane kalibracyjne i zaktualizuj urządzenia
            const devicesWithCalibrations = await fetchCalibrationData(processedData);
            setProcessedDevices(devicesWithCalibrations);

        } catch (error) {
            console.error('Error fetching test devices:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchServiceDevices = async () => {
        try {
            const response = await publicInstance.get('/view_dacapi_inventory_temp_all');
            console.log('Raw service data sample:', response.data.slice(0, 3).map(d => ({ box: d.box, mobile_operator: d.mobile_operator })));
            
            // Process service devices with the same field mapping
            const processedData = response.data.map((device: any) => ({
                box: device.box.toString(),
                iccid: device.iccid,
                time_stamp: device.time_stamp,
                label: Array.isArray(device.label) ? device.label : [],
                comments: device.comments,
                time_stamps: device.time_stamps,
                status: 'SERWIS',
                operator: normalizeOperator(device.mobile_operator),
                modem: device.modem
            }));

            // Pobierz dane kalibracyjne i zaktualizuj urządzenia
            const devicesWithCalibrations = await fetchCalibrationData(processedData);
            setServiceDevices(devicesWithCalibrations);
        } catch (error) {
            console.error('Error fetching service devices:', error);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        abortController.current = new AbortController();
        fetchTestDevices();
        fetchServiceDevices();
        return () => {
            isMounted.current = false;
            if (abortController.current) {
                abortController.current.abort();
            }
        };
    }, []);

    // Używamy useCallback dla funkcji zmiany widoku (list/charts)
    const handleViewChange = useCallback((_, newValue: 'list' | 'charts' | 'labelCharts') => {
        setActiveView(newValue);
    }, []);

    const toggleFilterListBySelection = useCallback(() => {
        setFilterListBySelection(prev => !prev);
    }, []);

    const handleViewHistory = useCallback(() => {
        navigate('/inventory/history');
    }, [navigate]);

    // Scripts panel handlers
    const handleOpenScriptsPanel = useCallback(() => {
        setScriptsPanelOpen(true);
    }, []);

    const handleCloseScriptsPanel = useCallback(() => {
        setScriptsPanelOpen(false);
    }, []);

    // Przygotuj listę urządzeń do wyświetlenia
    const devicesForList = useMemo(() => {
        if (filterListBySelection && selectedDevices.length > 0) {
            const selectedSet = new Set(selectedDevices);
            return {
                processed: processedDevices.filter(d => selectedSet.has(d.box)),
                service: serviceDevices.filter(d => selectedSet.has(d.box)),
            };
        }
        return {
            processed: processedDevices,
            service: serviceDevices,
        };
    }, [filterListBySelection, selectedDevices, processedDevices, serviceDevices]);

    // Zbierz wszystkie numery boxów dla ControlPanel
    const allDeviceBoxes = useMemo(() => [
        ...processedDevices.map(device => device.box),
        ...serviceDevices.map(device => device.box)
    ], [processedDevices, serviceDevices]);

    // Effect to clear selected devices when selection mode is turned off
    useEffect(() => {
        if (!selectionMode) {
            setSelectedDevices([]);
            setScriptsPanelOpen(false);
            setFilterListBySelection(false);
        }
    }, [selectionMode]);

    // Derive unique labels from all devices
    const uniqueLabels = useMemo(() => {
        const labelsSet = new Set<string>();
        
        processedDevices.forEach(device => {
            if (device.label) {
                device.label.forEach(labelObj => {
                    if (labelObj.label) {
                        labelsSet.add(labelObj.label);
                    }
                });
            }
        });
        
        return Array.from(labelsSet).sort();
    }, [processedDevices]);

    return (
        <Box sx={{ p: 3 }}>
            {isLoading && (
                <LinearProgress sx={{ mb: 2 }} />
            )}
            {processedDevices.length > 0 && (
                <>
                    <Paper sx={{ mb: 1.5 , border: `1px solid ${theme.palette.borders.primary}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', }}>
                            <Tabs
                                value={activeView}
                                onChange={handleViewChange}
                                sx={{ flexGrow: 1 }}
                            >
                                <Tab 
                                    icon={<ViewListIcon />} 
                                    label="Devices List" 
                                    value="list" 
                                    iconPosition="start"
                                />
                                <Tab 
                                    icon={<ShowChartIcon />} 
                                    label="Charts View" 
                                    value="charts" 
                                    iconPosition="start"
                                />
                                <Tab 
                                    icon={<LabelIcon />} 
                                    label="Label Charts" 
                                    value="labelCharts" 
                                    iconPosition="start"
                                />
                            </Tabs>
                        </Box>
                    </Paper>

                    {activeView === 'list' ? (
                        <>
                            <TestDevicesList 
                                processedDevices={devicesForList.processed}
                                serviceDevices={devicesForList.service}
                                operatorCounts={operatorCounts}
                                onDevicesUpdated={() => {
                                    fetchTestDevices();
                                    fetchServiceDevices();
                                }}
                                selectionMode={selectionMode}
                                setSelectionMode={setSelectionMode}
                                selectedDevices={selectedDevices}
                                setSelectedDevices={setSelectedDevices}
                                allDeviceBoxes={allDeviceBoxes}
                                setAddDeviceDialogOpen={setAddDeviceDialogOpen}
                                onViewHistory={handleViewHistory}
                                onOpenScriptsPanel={handleOpenScriptsPanel}
                                onLoadCharts={handleLoadCharts}
                                filterListBySelection={filterListBySelection}
                                toggleFilterListBySelection={toggleFilterListBySelection}
                            />
                            <AddDeviceForm 
                                open={addDeviceDialogOpen}
                                onClose={() => setAddDeviceDialogOpen(false)}
                                onDevicesAdded={() => {
                                    fetchTestDevices();
                                    setAddDeviceDialogOpen(false);
                                }}
                            />
                            <ScriptsPanel
                                selectedDevices={selectedDevices}
                                open={scriptsPanelOpen}
                                onClose={handleCloseScriptsPanel}
                            />
                        </>
                    ) : activeView === 'charts' ? (
                        <>
                            <Box sx={{ mt: 2 }}>
                                {/* Kontrolki w Grid */}
                                <Box 
                                    sx={{ 
                                        position: 'sticky',
                                        top: 20,
                                        zIndex: 1000,
                                        mb: 3,
                                        display: 'flex',
                                        gap: 2,
                                        flexWrap: 'wrap'
                                    }}
                                >
                                    <TimeRangeSelector 
                                        timeRange={timeRange}
                                        onTimeRangeChange={setTimeRange}
                                        onLoadCharts={devicesForCharts.length > 0 ? handleReloadChartsWithTimeRange : handleLoadCharts}
                                        selectedDevicesCount={selectedDevices.length}
                                        hasChartsLoaded={devicesForCharts.length > 0}
                                        isDarkMode={isDarkMode}
                                    />
                                    
                                    {/* Kontrolki natychmiastowe - pokazane tylko gdy mamy wykresy */}
                                    {devicesForCharts.length > 0 && (
                                        <DisplayOptionsSelector 
                                            selectedMetric={selectedMetric}
                                            onChange={(value) => setSelectedMetric(value)}
                                            chartsPerRow={chartsPerRow}
                                            onChartsPerRowChange={setChartsPerRow}
                                            isDarkMode={isDarkMode}
                                        />
                                    )}
                                </Box>
                                
                                <DeviceCharts
                                    processedDevices={devicesForCharts}
                                    deviceChartData={deviceChartData}
                                    deviceLoadings={deviceLoadings}
                                    isDarkMode={isDarkMode}
                                    selectedMetric={selectedMetric}
                                    chartsPerRow={chartsPerRow}
                                    timeRange={timeRange}
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            <LabelCharts
                                processedDevices={processedDevices}
                                uniqueLabels={uniqueLabels}
                                isDarkMode={isDarkMode}
                            />
                        </>
                    )}
                </>
            )}
        </Box>
    );
};

// Komponent dla time range i load charts
const TimeRangeSelector = ({ timeRange, onTimeRangeChange, onLoadCharts, selectedDevicesCount, hasChartsLoaded, isDarkMode }) => {
    const theme = useTheme();
    
    return (
        <Paper 
            sx={{ 
                p: 2,
                backdropFilter: 'blur(8px)',
                backgroundColor: theme => theme.name === 'dark'
                    ? 'rgba(38, 38, 38, 0.9)' 
                    : 'rgba(255, 255, 255, 0.9)',
                flex: '0 0 auto',
            }}
            elevation={3}
        >
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                Data Selection (requires reload)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl sx={{ width: 150 }}>
                    <InputLabel>Time Range</InputLabel>
                    <Select
                        value={timeRange}
                        onChange={(e) => onTimeRangeChange(Number(e.target.value))}
                        label="Time Range"
                        size="small"
                    >
                        <MenuItem value={1}>1 day</MenuItem>
                        <MenuItem value={2}>2 days</MenuItem>
                        <MenuItem value={3}>3 days</MenuItem>
                        <MenuItem value={7}>1 week</MenuItem>
                        <MenuItem value={14}>2 weeks</MenuItem>
                        <MenuItem value={30}>1 month</MenuItem>
                    </Select>
                </FormControl>

                <Button
                    variant="contained"
                    onClick={onLoadCharts}
                    disabled={!hasChartsLoaded && selectedDevicesCount === 0}
                    sx={{ minWidth: 120 }}
                >
                    {hasChartsLoaded ? 'Reload Charts' : 'Load Charts'}
                </Button>
            </Box>
            
            {!hasChartsLoaded && selectedDevicesCount === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Please select devices in Selection Mode first
                </Typography>
            )}
            
            {!hasChartsLoaded && selectedDevicesCount > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Ready to load charts for {selectedDevicesCount} selected device{selectedDevicesCount !== 1 ? 's' : ''}
                </Typography>
            )}
            
            {hasChartsLoaded && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Charts loaded. Change time range and click "Reload Charts" to update data.
                </Typography>
            )}
        </Paper>
    );
};

// Komponent dla display options
const DisplayOptionsSelector = ({ selectedMetric, onChange, chartsPerRow, onChartsPerRowChange, isDarkMode }) => {
    const theme = useTheme();
    
    return (
        <Paper 
            sx={{ 
                p: 2,
                backdropFilter: 'blur(8px)',
                backgroundColor: theme => theme.name === 'dark'
                    ? 'rgba(38, 38, 38, 0.9)' 
                    : 'rgba(255, 255, 255, 0.9)',
                flex: '0 0 auto',
            }}
            elevation={3}
        >
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                Display Options (instant update)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl sx={{ width: 200 }}>
                    <InputLabel>Select Metric</InputLabel>
                    <Select
                        value={selectedMetric}
                        onChange={(e) => onChange(e.target.value)}
                        label="Select Metric"
                        size="small"
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    maxHeight: 300
                                }
                            }
                        }}
                    >
                        {metricOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ width: 150 }}>
                    <InputLabel>Charts per Row</InputLabel>
                    <Select
                        value={chartsPerRow}
                        onChange={(e) => onChartsPerRowChange(Number(e.target.value))}
                        label="Charts per Row"
                        size="small"
                    >
                        <MenuItem value={1}>1</MenuItem>
                        <MenuItem value={2}>2</MenuItem>
                        <MenuItem value={3}>3</MenuItem>
                        <MenuItem value={4}>4</MenuItem>
                        <MenuItem value={6}>6</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        </Paper>
    );
};

export default InventoryPanel;
